import type { AdjustmentMode } from '@/domains/quick-updates/types/models/QuickInventoryUpdate.types'

import type { StockOutcome, StockOutcomeInput } from './stockOutcome.types'

/**
 * The most a shelf can hold. `inventory_adjustments.available_quantity` is a 32-bit integer, so a larger
 * figure fails the job that records it.
 */
const MAX_STOCK_QUANTITY = 2_147_483_647

const DELTA_BY_MODE: Record<AdjustmentMode, (current: number, quantity: number) => number> = {
  add: (_current, quantity) => quantity,
  remove: (_current, quantity) => -quantity,
  set: (current, quantity) => quantity - current
}

/**
 * What a change does to a shelf: the signed change, and what the shelf holds afterwards.
 *
 * The one place this arithmetic lives, so every preview of "36 → 48" and every change sent agree.
 * Set becomes a change here because the single adjust endpoint takes only a change. The batch endpoint
 * takes Set as a total and works the change out itself, at the moment it applies it.
 */
const stockOutcome = ({ current, mode, quantity }: StockOutcomeInput): StockOutcome => {
  const delta = DELTA_BY_MODE[mode](current, quantity)
  const after = current + delta
  const isOverLimit = after > MAX_STOCK_QUANTITY

  return {
    delta,
    after,
    // A removal that ends below zero, or any change that ends above what a shelf can hold. A shelf can
    // already sit below zero — a sale with `allow_out_of_stock` takes it there — and stock added to it
    // must still go through.
    isRefused: (delta < 0 && after < 0) || isOverLimit,
    isOverLimit,
    isUnchanged: delta === 0
  }
}

export { MAX_STOCK_QUANTITY, stockOutcome }
